// SurveyModal.jsx
import { useEffect, useRef, useState } from "react";
import "./SurveyNudge.css";

export default function SurveyModal({ product, surveyUrl, isMobile, delayMs = 30000 }) { // 30s default
  const [open, setOpen] = useState(false);
  const [showFab, setShowFab] = useState(false); // persistent "Leave feedback" button
  const firstFocusRef = useRef(null);
  const previousActiveEl = useRef(null);
  const timerRef = useRef(null);

  const eligible =
    ["HEJCIM_W13", "TSIFNP_W69", "EAHOAY_W53", "LQIXAH_W23"].includes(
      product?.id,
    ) && typeof window !== "undefined";

  // Always show FAB when eligible, and auto-open after delay
  useEffect(() => {
    setOpen(false);
    setShowFab(!!eligible); // <-- always show when eligible

    if (!eligible) return;

    timerRef.current = window.setTimeout(() => setOpen(true), delayMs);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [eligible, delayMs]);

  // Focus trap + ESC + body scroll lock
  useEffect(() => {
    if (!open) return;

    previousActiveEl.current = document.activeElement;
    document.body.style.overflow = "hidden";
    firstFocusRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === "Escape") dismiss();
      if (e.key === "Tab") {
        const focusable = Array.from(
          document.querySelectorAll(
            '[data-survey-modal] a, [data-survey-modal] button, [data-survey-modal] [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => !el.hasAttribute("disabled"));
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first) return;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first?.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
      previousActiveEl.current?.focus?.();
    };
  }, [open]);

  const dismiss = () => {
    setOpen(false);
    setShowFab(!!eligible); // keep FAB visible after closing
  };

  return (
    <>
      {/* Always-visible FAB when eligible (hidden only while modal is open) */}
      {/* {showFab && !open && (
        <button
          type="button"
          className="survey-fab"
          aria-label="Leave feedback about this product"
          onClick={() => setOpen(true)}
        >
          <span className="fab-emoji" aria-hidden>
            💬
          </span>
          <span className="fab-label">
            Leave
            <br />
            Feedback
          </span>
        </button>
      )} */}
      {showFab && !open && (
        <section
          className={`fab-container ${isMobile ? "mobile" : ""}`}
          aria-live="polite"
        >
          <div className="fab-stack">
            <div
              type="button"
              className="fab-btn"
              aria-label="Leave feedback about this product"
              onClick={() => setOpen(true)}
            >
              <span className="fab-icon" aria-hidden>
                📝
              </span>
              {!isMobile ? (
                <span className="fab-tooltip">Leave Feedback</span>
              ) : null}
            </div>

            <div
              type="button"
              className="fab-close"
              aria-label="Hide feedback button"
              onClick={(e) => {
                e.stopPropagation(); // don’t trigger the FAB underneath
                setShowFab(false);
              }}
            >
              ✕
            </div>
          </div>
        </section>
      )}

      {open && (
        <div
          className="survey-overlay"
          /* onClick={(e) => {
            if (e.target === e.currentTarget) dismiss();
          }} */
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="survey-title"
            aria-describedby="survey-desc"
            data-survey-modal
            className="survey-modal"
          >
            <button
              className="survey-close"
              aria-label="Dismiss survey invitation"
              onClick={dismiss}
            >
              ✕
            </button>

            <div className="survey-header">
              <span className="survey-icon" aria-hidden>
                📝
              </span>
              <h2 id="survey-title">Your opinion is very important to us!</h2>
            </div>

            <p id="survey-desc" className="survey-text">
              Got a minute to answer a few questions about Digital Product
              Passport?
            </p>

            <div className="survey-actions">
              <a
                ref={firstFocusRef}
                href={surveyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="survey-btn survey-btn-primary"
                onClick={dismiss}
              >
                Take the survey
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
